import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { storageBaseUrl } from '../../api';

const fmt = (n) => Number(n).toLocaleString();
const docNo = (prefix, id, date) => {
    const d = date ? new Date(date) : new Date();
    return `${prefix}-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}-${String(id).padStart(3,'0')}`;
};

const PrinterIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 6 2 18 2 18 9" />
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
        <rect x="6" y="14" width="12" height="8" />
    </svg>
);

const ArrowLeft = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
    </svg>
);

const InvoiceViewPage = () => {
    const { projectId } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [project, setProject] = useState(null);
    const [paying, setPaying] = useState(false);
    const [payError, setPayError] = useState(null);
    const navigate = useNavigate();

    const load = () => {
        api.get(`projects/${projectId}`).then(r => setProject(r.data)).catch(console.error);
        api.get(`invoices/by_project/${projectId}`).then(r => setInvoice(r.data)).catch(console.error);
    };

    useEffect(load, [projectId]);

    const handlePay = async () => {
        setPaying(true);
        setPayError(null);
        try {
            const res = await api.post('paygent/initiate', { invoice_id: invoice.id });
            if (res.data.test_mode) {
                // テストモード: 即時支払い済みとして画面を更新
                load();
            } else if (res.data.url) {
                // 本番モード: ペイジェント決済画面へリダイレクト
                window.location.href = res.data.url;
            }
        } catch {
            setPayError('支払い処理に失敗しました。');
        } finally {
            setPaying(false);
        }
    };

    if (!invoice || !project) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    const contractor = project.contractor;
    const customer = project.customer;
    const condition = project.condition;
    const issuedAt = invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('ja-JP') : '';
    const subtotal = Math.round(invoice.amount / 1.1);
    const tax = invoice.amount - subtotal;

    const statusLabel = invoice.status === 'issued' ? '未払い'
        : invoice.status === 'paid' ? '支払い済み'
        : '送金完了';
    const statusBg = invoice.status === 'issued' ? '#fff3e0'
        : invoice.status === 'paid' ? '#e8f5e9'
        : '#e3f2fd';
    const statusColor = invoice.status === 'issued' ? '#f57c00'
        : invoice.status === 'paid' ? '#2e7d32'
        : '#1565c0';

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8', padding: '32px 0 60px' }}>
            <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 16px' }}>

                {/* ツールバー（印刷時非表示） */}
                <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#005c53', fontWeight: 500, fontSize: '0.9rem' }}
                    >
                        <ArrowLeft /> 戻る
                    </button>
                    <button
                        onClick={() => window.print()}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: '#005c53', border: '2px solid #005c53', borderRadius: 4, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                    >
                        <PrinterIcon /> 印刷する
                    </button>
                </div>

                {/* 書類本体 */}
                <div className="print-area" style={{ background: '#fff', borderRadius: 8, padding: '48px 44px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>

                    {/* ヘッダー：タイトル */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 40 }}>
                        <div style={{ width: 120, height: 56 }}>
                            {contractor?.logo_image && (
                                <img src={`${storageBaseUrl}/${contractor.logo_image}`} alt="ロゴ" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            )}
                        </div>
                        <h1 style={{ fontSize: '2.2rem', fontWeight: 700, color: '#1a1a2e', margin: 0, letterSpacing: 8 }}>
                            請　求　書
                        </h1>
                    </div>

                    {/* 宛名 */}
                    <div style={{ marginBottom: 28 }}>
                        {customer?.postal_code && <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: 0 }}>〒{customer.postal_code}</p>}
                        {(customer?.prefecture || customer?.city) && <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: 0 }}>{customer?.prefecture}{customer?.city}</p>}
                        {customer?.address && <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '0 0 4px' }}>{customer.address}{customer.building ? `　${customer.building}` : ''}</p>}
                        <p style={{ fontSize: '1.15rem', fontWeight: 600, color: '#1a1a2e', margin: 0 }}>
                            {customer?.last_name} {customer?.first_name} 御中
                        </p>
                    </div>

                    {/* 書類情報 + 発行者 */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 36 }}>
                        {/* 左: メタ情報 */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.84rem' }}>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <span style={{ color: '#6b7280', minWidth: 80 }}>請求日:</span>
                                <span style={{ color: '#1a1a2e' }}>{issuedAt}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <span style={{ color: '#6b7280', minWidth: 80 }}>請求番号:</span>
                                <span style={{ color: '#1a1a2e' }}>{docNo('I', invoice.id, invoice.created_at)}</span>
                            </div>
                            {invoice.due_date && (
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <span style={{ color: '#6b7280', minWidth: 80 }}>支払期限:</span>
                                    <span style={{ color: '#1a1a2e' }}>{invoice.due_date}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                                <span style={{
                                    padding: '2px 10px', borderRadius: 12,
                                    fontSize: '0.76rem', fontWeight: 500,
                                    background: statusBg, color: statusColor,
                                }}>
                                    {statusLabel}
                                </span>
                            </div>
                        </div>

                        {/* 右: 発行者情報 */}
                        <div style={{ border: '1px solid #e0e4ea', borderRadius: 8, padding: '14px 16px', position: 'relative' }}>
                            <p style={{ fontWeight: 700, color: '#1a1a2e', margin: '0 0 6px', fontSize: '0.95rem' }}>{contractor?.company_name}</p>
                            <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0 0 2px' }}>担当：{contractor?.representative}</p>
                            {contractor?.postal_code && <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0 0 2px' }}>〒{contractor.postal_code}</p>}
                            <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: 0 }}>{contractor?.prefecture}{contractor?.city}{contractor?.address}</p>
                            {contractor?.seal_image ? (
                                <img
                                    src={`${storageBaseUrl}/${contractor.seal_image}`}
                                    alt="印鑑"
                                    style={{ width: 60, height: 60, objectFit: 'contain', position: 'absolute', bottom: 12, right: 14, opacity: 0.82, mixBlendMode: 'multiply' }}
                                />
                            ) : (
                                <div style={{ width: 52, height: 52, border: '1px solid #e0e4ea', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', color: '#9ca3af', position: 'absolute', bottom: 10, right: 12 }}>印</div>
                            )}
                        </div>
                    </div>

                    {/* 件名 */}
                    <p style={{ fontSize: '0.9rem', color: '#1a1a2e', marginBottom: 20 }}>
                        下記の通り御請求申し上げます。
                    </p>
                    {condition && (
                        <p style={{ fontSize: '0.9rem', color: '#1a1a2e', marginBottom: 20, fontWeight: 500 }}>
                            件名：{customer?.last_name} {customer?.first_name} 様　{condition.work_types?.join('・')}
                            {condition.location_city && `（${condition.location_city}）`}
                        </p>
                    )}

                    {/* 明細テーブル */}
                    <div style={{ marginBottom: 28, overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                            <thead>
                                <tr style={{ background: '#f5f6f8' }}>
                                    {['品目', '数量', '単位', '単価', '小計'].map((h, i) => (
                                        <th key={h} style={{
                                            border: '1px solid #e0e4ea', padding: '10px 12px',
                                            textAlign: i === 0 ? 'left' : 'right',
                                            color: '#1a1a2e', fontWeight: 600,
                                            width: i === 0 ? 'auto' : i === 1 ? 60 : i === 2 ? 56 : 100,
                                            whiteSpace: 'nowrap',
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.breakdown?.length > 0 ? invoice.breakdown.map((item, i) => {
                                    const rowAmt = item.amount ?? Math.round((item.quantity || 1) * (item.unit_price || 0));
                                    return (
                                        <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                            <td style={{ border: '1px solid #e0e4ea', padding: '10px 12px', color: '#1a1a2e' }}>{item.name}</td>
                                            <td style={{ border: '1px solid #e0e4ea', padding: '10px 12px', textAlign: 'right', color: '#1a1a2e' }}>{item.quantity ?? 1}</td>
                                            <td style={{ border: '1px solid #e0e4ea', padding: '10px 12px', textAlign: 'right', color: '#1a1a2e' }}>{item.unit ?? '式'}</td>
                                            <td style={{ border: '1px solid #e0e4ea', padding: '10px 12px', textAlign: 'right', color: '#1a1a2e' }}>¥{fmt(item.unit_price ?? rowAmt)}</td>
                                            <td style={{ border: '1px solid #e0e4ea', padding: '10px 12px', textAlign: 'right', color: '#1a1a2e' }}>¥{fmt(rowAmt)}</td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={5} style={{ border: '1px solid #e0e4ea', padding: '10px 12px', color: '#9ca3af', textAlign: 'center' }}>—</td>
                                    </tr>
                                )}
                                {/* 小計行 */}
                                <tr>
                                    <td colSpan={4} style={{ border: '1px solid #e0e4ea', padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#1a1a2e' }}>小計</td>
                                    <td style={{ border: '1px solid #e0e4ea', padding: '10px 12px', textAlign: 'right', color: '#1a1a2e' }}>¥{fmt(subtotal)}</td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={{ border: '1px solid #e0e4ea', padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#1a1a2e' }}>消費税（10%）</td>
                                    <td style={{ border: '1px solid #e0e4ea', padding: '10px 12px', textAlign: 'right', color: '#1a1a2e' }}>¥{fmt(tax)}</td>
                                </tr>
                                <tr style={{ background: '#f5f6f8' }}>
                                    <td colSpan={4} style={{ border: '1px solid #e0e4ea', padding: '12px', textAlign: 'right', fontWeight: 700, color: '#1a1a2e', fontSize: '0.95rem' }}>合計金額</td>
                                    <td style={{ border: '1px solid #e0e4ea', padding: '12px', textAlign: 'right', fontWeight: 700, color: '#1a1a2e', fontSize: '0.95rem' }}>¥{fmt(invoice.amount)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* 備考 */}
                    <div style={{ border: '1px solid #e0e4ea', borderRadius: 6, padding: '14px 16px', minHeight: 64 }}>
                        <p style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a1a2e', margin: '0 0 6px' }}>備考</p>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: 0, whiteSpace: 'pre-wrap' }}>{invoice.note || '—'}</p>
                    </div>
                </div>

                {/* 支払いアクション（印刷時非表示） */}
                {invoice.status === 'issued' && (
                    <div className="no-print" style={{ marginTop: 20 }}>
                        <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: '0.875rem', color: '#1565c0' }}>
                            支払い後、工事完了の「完了手続き」をするまで業者への送金は保留されます。
                        </div>
                        {payError && (
                            <p style={{ color: '#d32f2f', fontSize: '0.875rem', marginBottom: 12 }}>{payError}</p>
                        )}
                        <button
                            onClick={handlePay}
                            disabled={paying}
                            style={{ width: '100%', padding: '14px', background: paying ? '#9ca3af' : '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 700, fontSize: '1rem', cursor: paying ? 'default' : 'pointer' }}
                        >
                            {paying ? '処理中...' : '支払いへ進む'}
                        </button>
                    </div>
                )}

                {invoice.status === 'paid' && (
                    <div className="no-print" style={{ marginTop: 20, background: '#e8f5e9', border: '1px solid #81c784', borderRadius: 8, padding: '14px 16px', fontSize: '0.875rem', color: '#1b5e20' }}>
                        支払いが完了しました。工事完了後に「完了手続き」をすると業者への送金が行われます。
                    </div>
                )}
            </div>

            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    body { background: white !important; }
                    @page { size: A4; margin: 15mm; }
                }
                @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
        </div>
    );
};

export default InvoiceViewPage;
